import {
  InMemoryCache,
  type FieldFunctionOptions,
  type InMemoryCacheConfig,
} from '@apollo/client';
import type { HirerApolloProviderProps } from '@seek/hirer-graphql-react';
import type { GraphQLFormattedError } from 'graphql';

import {
  API_ENDPOINT_HIRER_GRAPH,
  APP_NAME,
  SITE,
} from 'src/app/shared/constants/configObjectProperties';
import type { OrderLineItem } from 'src/graphql/generated';

import { getConfig } from '../config';
import { isAbortError } from '../isAbortError';
import { log } from '../logging';

// @TODO what are we doing with this log metrics?
// const logMetrics = (metadata: {
//   durationMs: number;
//   operationName: string | null;
//   errorMessage: string | null;
// }) => {
//   const { durationMs, operationName, errorMessage } = metadata;
//   log.info('ApolloClient | Metrics', {
//     duration: durationMs,
//     requestPath: operationName,
//     httpMethod: 'post',
//     statusCode: undefined,
//     errorMessage: errorMessage ?? undefined,
//     source: 'graph',
//   });
// };

type RequestMetadata = Parameters<
  NonNullable<HirerApolloProviderProps['logError']>
>[2];

const logError = (
  error: unknown,
  operationName?: string,
  metadata?: RequestMetadata,
) => {
  if (error && Object.keys(error as Error).length > 0) {
    if (isAbortError(error)) {
      // ignore abort error
      return;
    }

    // Note: An exception to log.error made here to include operation name as part of a dynamic message.
    // This has been considered appropriate here given the operation name isn't vastly dynamic and will
    // allow error analysis and reporting more easily for this log type
    log.error(`ApolloClient | Error | ${operationName}`, {
      error,
      operationName,
      metadata,
    });
  }
};

const logGraphQLError = (
  error: GraphQLFormattedError,
  operationName?: string,
  metadata?: RequestMetadata,
) => {
  // Note: An exception to log.error made here to include operation name as part of a dynamic message.
  // This has been considered appropriate here given the operation name isn't vastly dynamic and will
  // allow error analysis and reporting more easily for this log type
  log.error(
    `ApolloClient | GraphQL Error | ${operationName}`,
    {
      error,
      operationName,
      metadata,
    },
    true,
  );
};

export const cacheConfig: InMemoryCacheConfig = {
  typePolicies: {
    HeaderFooterContext: {
      keyFields: ['user', ['id']],
    },
    CurrentUserContext: {
      keyFields: ['email'],
    },
    ContractBalance: {
      keyFields: [],
    },
    CurrentAdvertiserContext: {
      keyFields: ['seekId'],
    },
    Identity: {
      keyFields: ['subject', ['userId']],
    },
    OrderSummary: {
      keyFields: ['orderId'],
      fields: {
        lineItems: {
          merge(
            existing: OrderLineItem[],
            incoming: OrderLineItem[],
            { readField }: FieldFunctionOptions,
          ) {
            if (!existing) return incoming;

            const incomingHasPriceOrProduct = incoming.some(
              (item) => readField('product', item) || readField('price', item),
            );

            return incomingHasPriceOrProduct ? incoming : existing;
          },
        },
      },
    },
  },
};

export const apolloConfig = {
  appName: getConfig(APP_NAME),
  site: getConfig(SITE),
  logError,
  logGraphQLError,
  uri: getConfig(API_ENDPOINT_HIRER_GRAPH),
  cache: new InMemoryCache(cacheConfig),
};
